import React, { useEffect, useMemo, useState } from "react"
import { Container, Row, Col, Modal, Label, Input } from "reactstrap"
import Select from "react-select"
import axios from "axios"
import Swal from "sweetalert2"
import moment from "moment"
import { useSelector } from "react-redux"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import configure from "configure"
import { fetchData, fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import { colourStyles } from "utils/common-options"
import "./bankStatementConsole.css"

// What an entry can be matched to depends on which side of the statement it's
// on. A credit row is money that came INTO the bank, a debit row is money
// that went OUT — so "Overdraft (Withdraw)" (drawing cash into our account)
// only makes sense on a credit row, and "Overdraft (Deposit)" (paying the OD
// back down) only makes sense on a debit row. Shareholder movements are
// acknowledgement-only in both directions — they never create a real ledger
// entry anywhere else, this bank statement row IS the record.
const CREDIT_MATCH_TYPES = [
  { value: "invoice", label: "Invoice Payment" },
  { value: "overdraft_withdraw", label: "Overdraft (Withdraw)" },
  { value: "shareholder_investment", label: "Shareholder Investment" },
]
const DEBIT_MATCH_TYPES = [
  { value: "overdraft_deposit", label: "Overdraft (Deposit)" },
  { value: "shareholder_return", label: "Shareholder Return" },
  { value: "expense", label: "Expense" },
]
const SHAREHOLDER_TYPES = ["shareholder_investment", "shareholder_return"]

const MATCH_TYPE_LABELS = Object.fromEntries(
  [...CREDIT_MATCH_TYPES, ...DEBIT_MATCH_TYPES].map(t => [t.value, t.label])
)

// The bank whose statement is being imported is now sourced from the real
// finance-modes list (payment_via === "1" marks a row as a bank account,
// vs cash/credit-card/etc). We keep BOTH the numeric id (for payment_mode_id
// / payment_method_id when creating expense/invoice records — it must be the
// tblpayment_finance.id, not the string "1") AND the lower-cased payment_mode
// label as bank_name for fetching / uploading statement rows. payment_via is
// always "1" for a bank statement row — the specific bank is payment_method_id.

const BankStatementConsole = () => {
  document.title = "Bank Statement Reconsile | Zithas Crm"
  const token = secureLocalStorage.getItem("auth_token")
  const [loading, setLoading] = useState(false)

  const [bankOptions, setBankOptions] = useState([])
  const [selectedBank, setSelectedBank] = useState(null)
  const [entries, setEntries] = useState([])
  const [file, setFile] = useState(null)

  // Global counts (across every bank) so "how much is left" is visible even
  // before a bank is selected — the per-bank `entries` list can't answer
  // that on its own.
  const [summary, setSummary] = useState({ total: 0, matched: 0, unmatched: 0 })

  // list toolbar / pagination — entries are now fetched a page at a time from
  // the server (some banks have far too many statement rows to load and
  // slice client-side in one shot), so `total`/`counts` come back from the
  // API alongside the current page's rows instead of being derived locally.
  const [activeTab, setActiveTab] = useState("unmatched") // unmatched | matched
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [tabCounts, setTabCounts] = useState({ matched: 0, unmatched: 0 })

  // Overdraft accounts — only used inside the Match modal's Overdraft option,
  // separate from the statement-format bank selected above.
  const [odBanks, setOdBanks] = useState([])

  const [matchModal, setMatchModal] = useState(false)
  const [activeEntry, setActiveEntry] = useState(null)
  const [matchType, setMatchType] = useState(null)

  // invoice fields
  const [invoiceSource, setInvoiceSource] = useState("zithas")
  const [invoiceOptions, setInvoiceOptions] = useState([])
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  // expense fields
  const [expenseAction, setExpenseAction] = useState("new")
  const [categoryOptions, setCategoryOptions] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [expenseTitle, setExpenseTitle] = useState("")
  const [expenseLocation, setExpenseLocation] = useState(null)
  const [expenseOptions, setExpenseOptions] = useState([])
  const [selectedExpense, setSelectedExpense] = useState(null)

  // overdraft fields — direction (withdraw vs deposit) now comes from which
  // matchType was picked, not a separate radio
  const [odAccount, setOdAccount] = useState(null)

  // invoice fields (continued)
  const [tds, setTds] = useState("")
  const [transactionRef, setTransactionRef] = useState("")

  // shareholder fields — acknowledgement only
  const [shareholderName, setShareholderName] = useState("")

  // shared fields
  const [amount, setAmount] = useState("")
  const [matchDate, setMatchDate] = useState("")
  const [notes, setNotes] = useState("")

  let getLocations = useSelector((state) => state?.location?.locations)
  getLocations = useMemo(
    () => getLocations?.filter((l) => configure.location.includes(l.value)) || [],
    [getLocations]
  )

  const fetchOdBanks = async () => {
    const res = await fetchNodeData(setLoading, "overdraftBanks", token)
    if (res?.success) {
      setOdBanks(
        res?.data?.banks?.map(b => ({
          value: b.id,
          label: `${b.bank_name} - ${b.account_no}`,
          ...b,
        })) || []
      )
    }
  }

  // Debounce the search box so it doesn't fire a server request on every
  // keystroke — 400ms after the user stops typing is enough to feel instant
  // without hammering the API while they're still mid-word.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(t)
  }, [search])

  const fetchEntries = async bankName => {
    if (!bankName) return
    const params = new URLSearchParams({
      page: String(page),
      limit: String(pageSize),
      status: activeTab,
    })
    if (debouncedSearch) params.set("search", debouncedSearch)
    const res = await fetchNodeData(setLoading, `bankStatement/${bankName}?${params.toString()}`, token)
    // Accept the response by shape (an array under `data`) rather than
    // requiring `success: true` — some deployments of this endpoint have
    // been seen returning `{ data: [...] }` without the success flag, which
    // silently dropped every entry here even though the data was correct.
    if (Array.isArray(res?.data)) {
      setEntries(res.data)
      setTotal(res?.pagination?.total || 0)
      setTabCounts({ matched: res?.counts?.matched || 0, unmatched: res?.counts?.unmatched || 0 })
    }
  }

  const fetchSummary = async () => {
    const res = await fetchNodeData(() => {}, "bankStatement/summary/counts", token)
    if (res?.success) {
      setSummary({ total: res.total, matched: res.matched, unmatched: res.unmatched })
    }
  }

  useEffect(() => {
    fetchOdBanks()
    fetchSummary()
    fetchNodeData(setLoading, "financeCategory", token).then(res => {
      if (res?.success) {
        setCategoryOptions(res?.data?.map(c => ({ value: c.id, label: c.name })) || [])
      }
    })
    // Bank list = finance modes flagged as a bank account (payment_via "1").
    // Keep both the numeric id (for payment_mode_id) and the lower-cased
    // payment_mode label as bank_name for statement fetch/upload. The
    // statement row itself is always payment_via=1 (Bank), the specific bank
    // is identified by its id.
    fetchNodeData(() => {}, "financeModes", token).then(res => {
      if (res?.success) {
        const banks = (res?.data || []).filter(m => String(m.payment_via) === "1")
        setBankOptions(
          banks.map(m => ({
            value: m.id,
            label: m.payment_mode,
            bank_name: (m.payment_mode || "").trim().toLowerCase(),
            payment_via: String(m.payment_via),
          }))
        )
      }
    })
  }, [])

  useEffect(() => {
    fetchEntries(selectedBank?.bank_name)
  }, [selectedBank, page, pageSize, activeTab, debouncedSearch])

  const onUpload = async () => {
    if (!selectedBank) {
      Swal.fire({ icon: "warning", text: "Please select a bank first." })
      return
    }
    if (!file) {
      Swal.fire({ icon: "warning", text: "Please choose an Excel file to upload." })
      return
    }
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append("bank_name", selectedBank.bank_name)
      formData.append("file", file)
      const res = await axios.post(`${configure.nodeApi}/bankStatement/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (Array.isArray(res?.data?.data)) {
        Swal.fire({ icon: "success", text: res?.data?.message || "Upload complete" })
        setFile(null)
        // Upload's own response is the bank's full unpaginated list (used
        // for the "N imported" count) — refetch through the paginated
        // endpoint instead of dumping that straight into `entries`.
        fetchEntries(selectedBank?.bank_name)
        fetchSummary()
      } else {
        Swal.fire({ icon: "error", text: res?.data?.message || "Upload failed" })
      }
    } catch (err) {
      Swal.fire({ icon: "error", text: err?.response?.data?.message || err?.message })
    } finally {
      setLoading(false)
    }
  }

  const openMatchModal = entry => {
    setActiveEntry(entry)
    setMatchType(null)
    setInvoiceSource("zithas")
    setSelectedInvoice(null)
    setInvoiceOptions([])
    setTds("")
    // Pre-fill from the bank statement's own reference number (cheque/UTR/etc)
    // — it's the natural transaction reference for an invoice payment created
    // from this row, but still editable in case it needs correcting.
    setTransactionRef(entry?.reference_no || "")
    setExpenseAction("new")
    setSelectedCategory(null)
    setExpenseTitle("")
    setExpenseLocation(null)
    setSelectedExpense(null)
    setExpenseOptions([])
    setOdAccount(null)
    setShareholderName("")
    setNotes("")
    setAmount(Number(entry?.credit) > 0 ? entry?.credit : entry?.debit)
    setMatchDate(entry?.statement_date ? moment(entry.statement_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"))
    setMatchModal(true)
  }

  const isCreditEntry = Number(activeEntry?.credit) > 0
  const matchTypeOptions = isCreditEntry ? CREDIT_MATCH_TYPES : DEBIT_MATCH_TYPES

  const onSelectMatchType = type => {
    setMatchType(type)
    if (type?.value === "invoice") {
      fetchNodeData(setLoading, "zithasInvoice", token).then(res => {
        if (res?.status == 200) {
          setInvoiceOptions(
            res?.data?.map(i => ({
              value: i.id,
              label: `${i.invoice_number} - ${i.customer_name || ""} (Due: ${i.currency_symbol || ""}${Number(i.balance_due).toFixed(2)} ${i.currency_name || ""})`,
            })) || []
          )
        }
      })
    }
    if (type?.value === "expense") {
      fetchData(setLoading, "budgetExpense/2/1", token).then(res => {
        if (res?.success) {
          setExpenseOptions(
            res?.data?.expense?.map(e => ({
              value: e.id,
              label: `${e.title} (Due: ${(Number(e.amount) - Number(e.tbal)).toFixed(2)})`,
            })) || []
          )
        }
      })
    }
  }

  const onChangeInvoiceSource = source => {
    setInvoiceSource(source)
    setSelectedInvoice(null)
    fetchNodeData(setLoading, source === "ztpl" ? "ztplInvoice" : "zithasInvoice", token).then(res => {
      if (res?.status == 200) {
        setInvoiceOptions(
          res?.data?.map(i => ({
            value: i.id,
            label: `${i.invoice_number || i.tax_inv_no} - ${i.customer_name || ""} (Due: ${i.currency_symbol || ""}${Number(i.balance_due).toFixed(2)} ${i.currency_name || ""})`,
          })) || []
        )
      }
    })
  }

  const onSubmitMatch = async () => {
    if (!matchType) {
      Swal.fire({ icon: "warning", text: "Please select where this entry belongs." })
      return
    }
    if (!selectedBank) {
      Swal.fire({ icon: "warning", text: "Please select a bank again — the bank context was lost." })
      return
    }

    const body = {
      entry_id: activeEntry.id,
      matched_type: matchType.value,
      shareholder_name: shareholderName || null,
      notes: notes || null,
    }

    if (matchType.value === "invoice") {
      if (!selectedInvoice) {
        Swal.fire({ icon: "warning", text: "Please select an invoice." })
        return
      }
      // selectedBank.label is the real payment_mode label (e.g. "HDFC - 50200006048052")
      // selectedBank.value is the numeric tblpayment_finance.id (needed for future linking if used)
      // Backend will reverse the INR entry amount to foreign amount using Frankfurter rate
      // for the payment date when the invoice currency is not INR (id 2). See
      // backfill-exchange-rate.js for the same API shape.
      Object.assign(body, {
        invoice_type: invoiceSource,
        invoice_id: selectedInvoice.value,
        payment_mode: selectedBank.label || "Bank",
        payment_mode_id: selectedBank.value,
        amount: Number(amount),
        date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        tds: tds || 0,
        transaction_id: transactionRef || activeEntry?.reference_no || null,
      })
    } else if (matchType.value === "expense") {
      if (expenseAction === "new") {
        if (!selectedCategory || !expenseTitle) {
          Swal.fire({ icon: "warning", text: "Please fill category and title." })
          return
        }
        Object.assign(body, {
          expense_action: "new",
          category: selectedCategory.value,
          title: expenseTitle,
          payment_via: 1, // Bank — this row is coming straight off a bank statement
          payment_method_id: selectedBank.value, // <-- specific bank account id from tblpayment_finance
          amount: Number(amount),
          date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
          location: expenseLocation?.value || 2,
        })
      } else {
        if (!selectedExpense) {
          Swal.fire({ icon: "warning", text: "Please select an expense." })
          return
        }
        Object.assign(body, {
          expense_action: "existing",
          expense_id: selectedExpense.value,
          payment_mode_id: selectedBank.value, // <-- specific bank id, not hardcoded 1
          payment_method_id: selectedBank.value,
          payment_via: 1,
          amount: Number(amount),
          date: matchDate ? moment(matchDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        })
      }
    } else if (matchType.value === "overdraft_withdraw" || matchType.value === "overdraft_deposit") {
      if (!odAccount) {
        Swal.fire({ icon: "warning", text: "Please select an overdraft account." })
        return
      }
      Object.assign(body, {
        overdraft_bank_id: odAccount.value,
        amount: Number(amount),
      })
    }
    // shareholder_investment / shareholder_return need nothing beyond the
    // shared amount/date/notes already on the entry — acknowledgement only.

    const res = await FetchNodePostData(setLoading, "bankStatement/match", token, body)
    if (res?.data?.success) {
      Swal.fire({ icon: "success", text: "Entry matched successfully." })
      setMatchModal(false)
      fetchEntries(selectedBank?.bank_name)
      fetchSummary()
    } else {
      Swal.fire({ icon: "error", text: res?.data?.message || "Could not match this entry." })
    }
  }

  // Reclassifying a mistake: unmatch first (which reverses the overdraft
  // balance automatically if that's what it was, but does NOT delete an
  // invoice payment or expense record it created — those are real financial
  // records the user reviews/deletes manually if genuinely wrong), then
  // reopen the same match modal on the now-unprocessed entry.
  const onRematch = async entry => {
    const result = await Swal.fire({
      title: "Rematch this entry?",
      html: entry.matched_ref_table && entry.matched_ref_table !== "overdraft_data"
        ? `This will unlink the entry so you can reclassify it. The <b>${entry.matched_ref_table.replace(/_/g, " ")}</b> record it created (ID ${entry.matched_ref_id}) will NOT be deleted automatically — remove it from its own page first if this match was a genuine mistake.`
        : "This will undo the previous classification (reversing the overdraft balance if that's what it was) so you can pick a new one.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, unmatch it",
    })
    if (!result.isConfirmed) return

    const res = await FetchNodePostData(setLoading, "bankStatement/unmatch", token, { entry_id: entry.id })
    if (res?.data?.success) {
      await fetchEntries(selectedBank?.bank_name)
      fetchSummary()
      openMatchModal({ ...entry, status: "unprocessed", matched_type: null, matched_ref_id: null, matched_ref_table: null })
    } else {
      Swal.fire({ icon: "error", text: res?.data?.message || "Could not unmatch this entry." })
    }
  }

  const bankMappedCount = tabCounts.matched
  const bankUnmatchedCount = tabCounts.unmatched

  // Filtering/pagination now happen server-side (see fetchEntries) — `entries`
  // IS the current page already, `total` is the full filtered count from the
  // API.
  const pageEntries = entries
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentPage = Math.min(page, totalPages)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, selectedBank, pageSize, activeTab])

  // Windowed page numbers (1 … p-1 p p+1 … last) instead of listing every
  // page — with thousands of entries that flat list overflowed the whole
  // page horizontally and was unusable.
  const pageNumbers = useMemo(() => {
    const delta = 2
    const range = []
    const withDots = []
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i)
      }
    }
    let last = 0
    for (const i of range) {
      if (last && i - last > 1) withDots.push("...")
      withDots.push(i)
      last = i
    }
    return withDots
  }, [totalPages, currentPage])

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content">
        <Container fluid className="bsc-page">
          <Breadcrumbs title={"Reports"} breadcrumbItem={"Bank Statement Reconsile"} />


          <div className="bsc-stats">
            <div className="bsc-stat-card">
              <div className="bsc-stat-icon total">
                <i className="fas fa-list-ul"></i>
              </div>
              <div>
                <div className="bsc-stat-label">Total Entries (all banks)</div>
                <div className="bsc-stat-value">{summary.total}</div>
              </div>
            </div>
            <div className="bsc-stat-card">
              <div className="bsc-stat-icon mapped">
                <i className="fas fa-check-circle"></i>
              </div>
              <div>
                <div className="bsc-stat-label">Mapped Entries</div>
                <div className="bsc-stat-value">{summary.matched}</div>
              </div>
            </div>
            <div className="bsc-stat-card">
              <div className="bsc-stat-icon remaining">
                <i className="fas fa-hourglass-half"></i>
              </div>
              <div>
                <div className="bsc-stat-label">Remaining Entries</div>
                <div className="bsc-stat-value">{summary.unmatched}</div>
              </div>
            </div>
          </div>

          <div className="bsc-card">
            <Row className="align-items-end">
              <Col md={4}>
                <Label>Select Bank</Label>
                <Select
                  placeholder="Select Bank"
                  options={bankOptions}
                  value={selectedBank}
                  onChange={setSelectedBank}
                  styles={colourStyles}
                />
                {selectedBank && (
                  <small className="text-muted d-block mt-1">
                    Bank id <b>{selectedBank.value}</b> • This bank will be stored as payment_method_id (payment_via = 1) on any expense/invoice record created from a match.
                  </small>
                )}
              </Col>
              <Col md={4}>
                <Label>Upload Bank Statement (Excel)</Label>
                <Input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files[0])} />
              </Col>
              <Col md={2}>
                <button className="bsc-import-btn" onClick={onUpload}>
                  <i className="fas fa-upload me-2"></i>
                  Import
                </button>
              </Col>
            </Row>
          </div>

          <div className="bsc-card">
            <div className="bsc-tabs">
              <button
                className={`bsc-tab ${activeTab === "unmatched" ? "active" : ""}`}
                onClick={() => setActiveTab("unmatched")}
              >
                Unmatched {selectedBank ? `(${bankUnmatchedCount})` : ""}
              </button>
              <button
                className={`bsc-tab ${activeTab === "matched" ? "active" : ""}`}
                onClick={() => setActiveTab("matched")}
              >
                Matched {selectedBank ? `(${bankMappedCount})` : ""}
              </button>
            </div>

            <div className="bsc-toolbar">
              <div className="bsc-search">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Search by description or reference..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="bsc-page-size">
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                  {[10, 25, 50, 100].map(n => (
                    <option key={n} value={n}>Show {n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bsc-table-wrap">
              <table className="bsc-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageEntries.map(entry => (
                    <tr key={entry.id}>
                      <td>{entry.statement_date ? moment(entry.statement_date).format("DD-MM-YYYY") : ""}</td>
                      <td>{entry.reference_no}</td>
                      <td className="bsc-desc" title={entry.description}>{entry.description}</td>
                      <td className="text-end">{Number(entry.debit) ? `₹ ${Number(entry.debit).toFixed(2)}` : ""}</td>
                      <td className="text-end">{Number(entry.credit) ? `₹ ${Number(entry.credit).toFixed(2)}` : ""}</td>
                      <td className="text-center">
                        {entry.status === "matched" ? (
                          <span
                            className="bsc-pill matched"
                            title={[entry.shareholder_name, entry.notes].filter(Boolean).join(" — ") || undefined}
                          >
                            <i className="fas fa-check"></i>
                            {MATCH_TYPE_LABELS[entry.matched_type] || entry.matched_type}
                          </span>
                        ) : (
                          <span className="bsc-pill unprocessed">
                            <i className="fas fa-clock"></i>
                            Unprocessed
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        {entry.status !== "matched" ? (
                          <button className="bsc-match-btn" title="Match" onClick={() => openMatchModal(entry)}>
                            <i className="fas fa-handshake"></i>
                          </button>
                        ) : (
                          <button className="bsc-match-btn rematch" title="Rematch" onClick={() => onRematch(entry)}>
                            <i className="fas fa-rotate"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {pageEntries.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {!selectedBank
                          ? "Select a bank to view its statement."
                          : total > 0
                            ? "" // between pages while a fetch is in flight
                            : debouncedSearch
                              ? "No entries match your search."
                              : activeTab === "matched"
                                ? "No matched entries yet."
                                : "No unmatched entries for this bank yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="bsc-pagination">
                <div className="bsc-pagination-info">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, total)} of {total} entries
                </div>
                <div className="bsc-pagination-controls">
                  <button
                    className="bsc-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  {pageNumbers.map((n, i) =>
                    n === "..." ? (
                      <span key={`dots-${i}`} className="bsc-page-dots">…</span>
                    ) : (
                      <button
                        key={n}
                        className={`bsc-page-btn ${n === currentPage ? "active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    )
                  )}
                  <button
                    className="bsc-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>

      <Modal isOpen={matchModal} size="lg" centered toggle={() => setMatchModal(false)}>
        <div className="modal-header">
          <h5 className="modal-title mt-0">Match Entry</h5>
          <button type="button" onClick={() => setMatchModal(false)} className="btn-modal-close">
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <Row className="mb-3">
            <Col md={6}>
              <Label>Description</Label>
              <div className="text-muted">{activeEntry?.description}</div>
              {selectedBank && <small className="text-muted">Bank: {selectedBank.label} (id {selectedBank.value}, via 1)</small>}
            </Col>
            <Col md={3}>
              <Label>Amount (₹)</Label>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} />
              <small className="text-muted">Entry is in INR — for foreign-currency invoices the backend will reverse-convert using Frankfurter rate for the payment date (₹ / rate).</small>
            </Col>
            <Col md={3}>
              <Label>Date</Label>
              <Input type="date" value={matchDate} onChange={e => setMatchDate(e.target.value)} />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Label>This entry belongs to ({isCreditEntry ? "Credit" : "Debit"})</Label>
              <Select options={matchTypeOptions} value={matchType} onChange={onSelectMatchType} styles={colourStyles} />
            </Col>
          </Row>

          {matchType?.value === "invoice" && (
            <Row className="mb-3">
              <Col md={3}>
                <Label>Invoice Type</Label>
                <div className="d-flex gap-3 mt-2">
                  <div>
                    <input
                      type="radio"
                      id="src-zithas"
                      checked={invoiceSource === "zithas"}
                      onChange={() => onChangeInvoiceSource("zithas")}
                    />
                    <label htmlFor="src-zithas" className="ms-1">Zithas</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="src-ztpl"
                      checked={invoiceSource === "ztpl"}
                      onChange={() => onChangeInvoiceSource("ztpl")}
                    />
                    <label htmlFor="src-ztpl" className="ms-1">ZTPL</label>
                  </div>
                </div>
              </Col>
              <Col md={7}>
                <Label>Invoice</Label>
                <Select options={invoiceOptions} value={selectedInvoice} onChange={setSelectedInvoice} styles={colourStyles} />
              </Col>
              <Col md={2}>
                <Label>TDS</Label>
                <Input type="number" value={tds} onChange={e => setTds(e.target.value)} placeholder="0" />
              </Col>
              <Col md={4} className="mt-3">
                <Label>Transaction Ref</Label>
                <Input
                  type="text"
                  value={transactionRef}
                  onChange={e => setTransactionRef(e.target.value)}
                  placeholder="Cheque / UTR / transaction number"
                />
              </Col>
            </Row>
          )}

          {matchType?.value === "expense" && (
            <>
              <Row className="mb-3">
                <Col md={12}>
                  <div className="d-flex gap-3">
                    <div>
                      <input
                        type="radio"
                        id="exp-new"
                        checked={expenseAction === "new"}
                        onChange={() => setExpenseAction("new")}
                      />
                      <label htmlFor="exp-new" className="ms-1">Add New Expense</label>
                    </div>
                    <div>
                      <input
                        type="radio"
                        id="exp-existing"
                        checked={expenseAction === "existing"}
                        onChange={() => setExpenseAction("existing")}
                      />
                      <label htmlFor="exp-existing" className="ms-1">Pay Registered Expense</label>
                    </div>
                  </div>
                </Col>
              </Row>
              {expenseAction === "new" ? (
                <Row className="mb-3">
                  <Col md={4}>
                    <Label>Category</Label>
                    <Select options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} styles={colourStyles} />
                  </Col>
                  <Col md={5}>
                    <Label>Title</Label>
                    <Input type="text" value={expenseTitle} onChange={e => setExpenseTitle(e.target.value)} />
                  </Col>
                  <Col md={3}>
                    <Label>Location</Label>
                    <Select options={getLocations} value={expenseLocation} onChange={setExpenseLocation} styles={colourStyles} />
                  </Col>
                </Row>
              ) : (
                <Row className="mb-3">
                  <Col md={12}>
                    <Label>Expense</Label>
                    <Select options={expenseOptions} value={selectedExpense} onChange={setSelectedExpense} styles={colourStyles} />
                  </Col>
                </Row>
              )}
            </>
          )}

          {(matchType?.value === "overdraft_withdraw" || matchType?.value === "overdraft_deposit") && (
            <Row className="mb-3">
              <Col md={12}>
                <Label>Overdraft Account</Label>
                <Select options={odBanks} value={odAccount} onChange={setOdAccount} styles={colourStyles} />
              </Col>
            </Row>
          )}

          {SHAREHOLDER_TYPES.includes(matchType?.value) && (
            <Row className="mb-3">
              <Col md={12}>
                <Label>Shareholder Name</Label>
                <Input
                  type="text"
                  placeholder="Optional — for reference only"
                  value={shareholderName}
                  onChange={e => setShareholderName(e.target.value)}
                />
              </Col>
            </Row>
          )}

          {matchType && (
            <Row className="mb-3">
              <Col md={12}>
                <Label>Notes</Label>
                <Input
                  type="textarea"
                  rows={2}
                  placeholder="Optional — kept on this entry for acknowledgement, not required to save"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </Col>
            </Row>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-red btn-label me-1" onClick={() => setMatchModal(false)}>
            <i className="dripicons-cross me-2 icon-center label-icon"></i>
            Cancel
          </button>
          <button className="btn btn-purple btn-label" onClick={onSubmitMatch}>
            <i className="fas fa-check me-2 icon-size label-icon"></i>
            Confirm Match
          </button>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default BankStatementConsole
